import {
  Slider,
  Text,
  useTheme,
  styled,
  Box,
  Stack,
  Input,
} from '@ltvco/refresh-lib/theme';
import { useState, useEffect } from 'react';

const MIN_AGE = 18;
const MAX_AGE = 80;

const SliderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  marginBottom: theme.spacing(2),
  '.MuiSlider-root': {
    height: `${theme.spacing(1.25)}`,
    color: theme.palette.primary.main,
  },
  '& .MuiSlider-thumb': {
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&:before': {
      display: 'none',
    },
  },
}));

export interface AgeSliderProps {
  /** Current age value to sync with parent component */
  currentAge: number;
  /** Callback fired when the age changes. */
  onChange: (value: number | number[] | string) => void;
}

export function AgeInput({ currentAge, onChange }: AgeSliderProps) {
  const [age, setAge] = useState<string>(
    currentAge ? currentAge.toString() : ''
  );
  const theme = useTheme();

  useEffect(() => {
    setAge(currentAge ? currentAge.toString() : '');
  }, [currentAge]);

  const handleAgeChange = (_event: Event, newValue: number | number[]) => {
    const val = typeof newValue === 'number' ? newValue : newValue[0];
    onChange(val === MIN_AGE ? '' : val);
  };

  const ageLabelFormat = (value: number) => {
    if (value <= MIN_AGE) {
      return '18+';
    }
    if (value >= MAX_AGE) {
      return '80+';
    }
    const lowerBound = value > MIN_AGE + 1 ? value - 2 : MIN_AGE;
    const upperBound = value < MAX_AGE - 1 ? value + 2 : MAX_AGE;
    return `${lowerBound}-${upperBound}`;
  };

  const handleAgeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target?.value || '';
    const numVal = Number(val);
    setAge(val);
    if (val === '' || (numVal >= MIN_AGE && numVal <= MAX_AGE)) {
      onChange(val);
    }
  };

  return (
    <SliderContainer>
      <Text variant="h5" mt={2} mb={1}>
        Age Range
      </Text>

      <Stack direction="row" spacing={2} width="100%" pl={1}>
        <Slider
          value={currentAge}
          defaultValue={MIN_AGE}
          min={MIN_AGE}
          step={1}
          max={MAX_AGE}
          track="normal"
          onChange={handleAgeChange}
          valueLabelDisplay="off"
          aria-labelledby="track-false-slider"
          name="age"
          sx={{
            '& .MuiSlider-track': {
              backgroundColor: theme.palette.primary.main,
              height: '4px',
            },
            '& .MuiSlider-rail': {
              backgroundColor: theme.palette.primary.light,
              opacity: 1,
              height: '4px',
            },
            '& .MuiSlider-thumb': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        />
        <Input
          value={age}
          onChange={handleAgeInputChange}
          type="number"
          sx={{ width: '75px' }}
        />
      </Stack>
      <Text variant="caption"> {ageLabelFormat(currentAge)}</Text>
    </SliderContainer>
  );
}
